import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Inbox, Loader2, ImageIcon, Lock, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const KEY_STORAGE = "laa_admin_key";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const thCls = "text-[11px] tracking-[0.14em] uppercase text-[rgba(244,239,230,0.5)]";

const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(KEY_STORAGE, password);
      onSuccess(password);
    } catch {
      setError("Incorrect password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--laa-ink)] text-[color:var(--laa-ivory)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-[rgba(244,239,230,0.6)] hover:text-[color:var(--laa-ivory)] laa-link">
          <ArrowLeft size={15} /> Back to site
        </Link>
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="mt-6 rounded-2xl border border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)] p-8"
        >
          <Lock size={22} className="text-[color:var(--laa-champagne)]" />
          <h1 className="mt-5 text-2xl">Admin Access</h1>
          <p className="mt-2 text-sm text-[rgba(244,239,230,0.55)]">Enter the admin password to view submissions.</p>
          <Label className="mt-6 block text-xs tracking-[0.14em] uppercase text-[rgba(244,239,230,0.6)]">Password</Label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            placeholder="••••••••••••"
            className="mt-2 bg-[rgba(244,239,230,0.04)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] placeholder:text-[rgba(244,239,230,0.35)] focus-visible:ring-[color:var(--laa-champagne)] rounded-xl h-11"
            autoFocus
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-2 text-xs text-red-400">{error}</p>
          )}
          <Button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit-button"
            className="mt-6 w-full rounded-xl py-5 text-sm font-medium bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Verifying…</span>
            ) : (
              "Unlock"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const authHeaders = useCallback(() => ({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    setContacts([]);
  };

  useEffect(() => {
    if (!adminKey) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Key": adminKey } });
        if (!cancelled) setContacts(res.data);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(KEY_STORAGE);
          setAdminKey("");
        } else {
          setError("Failed to load submissions. Please refresh.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [adminKey]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      contacts.filter(
        (c) => !q || [c.name, c.email, c.message, c.reference].join(" ").toLowerCase().includes(q)
      ),
    [contacts, q]
  );

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const res = await axios.get(`${API}/admin/contacts/${id}`, authHeaders());
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (!adminKey) {
    return <AdminLogin onSuccess={setAdminKey} />;
  }

  return (
    <div className="min-h-screen bg-[color:var(--laa-ink)] text-[color:var(--laa-ivory)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-[rgba(244,239,230,0.6)] hover:text-[color:var(--laa-ivory)] laa-link">
            <ArrowLeft size={15} /> Back to site
          </Link>
          <button
            type="button"
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-[rgba(244,239,230,0.6)] hover:text-[color:var(--laa-ivory)] laa-link"
          >
            <LogOut size={14} /> Lock
          </button>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="laa-label">Internal</p>
            <h1 className="mt-2 text-3xl sm:text-4xl">Contact Messages</h1>
            <p className="mt-2 text-sm text-[rgba(244,239,230,0.5)]">{contacts.length} total submissions</p>
          </div>
          <Input
            data-testid="admin-submissions-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, message…"
            className="sm:w-72 bg-[rgba(244,239,230,0.04)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] placeholder:text-[rgba(244,239,230,0.35)] focus-visible:ring-[color:var(--laa-champagne)] rounded-xl"
          />
        </div>

        {loading ? (
          <div className="mt-20 flex items-center justify-center gap-3 text-[rgba(244,239,230,0.6)]" data-testid="admin-loading">
            <Loader2 size={18} className="animate-spin" /> Loading submissions…
          </div>
        ) : error ? (
          <p className="mt-20 text-center text-red-400" data-testid="admin-error">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-[rgba(244,239,230,0.45)]" data-testid="admin-contacts-empty">
            <Inbox size={28} className="mx-auto mb-3 opacity-60" />
            No contact messages{q ? " match your search" : " yet"}.
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-[color:var(--laa-hairline)] overflow-hidden">
            <Table data-testid="admin-submissions-table">
              <TableHeader>
                <TableRow className="border-[color:var(--laa-hairline)] hover:bg-transparent">
                  <TableHead className={thCls}>Reference</TableHead>
                  <TableHead className={thCls}>Name</TableHead>
                  <TableHead className={thCls}>Email</TableHead>
                  <TableHead className={thCls}>Message</TableHead>
                  <TableHead className={thCls}>Photos</TableHead>
                  <TableHead className={thCls}>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow
                    key={c.id}
                    data-testid="admin-submissions-row"
                    onClick={() => openDetail(c.id)}
                    className="border-[color:var(--laa-hairline)] cursor-pointer hover:bg-[rgba(244,239,230,0.04)]"
                  >
                    <TableCell className="text-[color:var(--laa-champagne)] text-xs tracking-wider">{c.reference || "—"}</TableCell>
                    <TableCell className="text-sm">{c.name}</TableCell>
                    <TableCell className="text-xs text-[rgba(244,239,230,0.6)]">{c.email}</TableCell>
                    <TableCell className="text-xs text-[rgba(244,239,230,0.55)] max-w-[280px] truncate">{c.message}</TableCell>
                    <TableCell className="text-sm text-[rgba(244,239,230,0.6)]">
                      <span className="inline-flex items-center gap-1.5">
                        <ImageIcon size={13} /> {c.photo_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-[rgba(244,239,230,0.5)]">{fmtDate(c.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Contact detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[color:var(--laa-ink-2)] border-[color:var(--laa-hairline)] text-[color:var(--laa-ivory)] max-w-2xl" data-testid="admin-contact-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl font-normal">
              {detailLoading ? "Loading…" : `Message from ${detail?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 size={20} className="animate-spin text-[rgba(244,239,230,0.6)]" />
            </div>
          ) : detail ? (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                {[
                  ["Reference", detail.reference || "—"],
                  ["Email", detail.email],
                  ["Received", fmtDate(detail.created_at)],
                  ["Email to Support", detail.email_status || "—"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] tracking-[0.16em] uppercase text-[rgba(244,239,230,0.45)]">{k}</p>
                    <p className="mt-1 text-[rgba(244,239,230,0.85)]">{v}</p>
                  </div>
                ))}
                <div className="col-span-2">
                  <p className="text-[10px] tracking-[0.16em] uppercase text-[rgba(244,239,230,0.45)]">Message</p>
                  <p className="mt-1 text-[rgba(244,239,230,0.75)] leading-relaxed whitespace-pre-wrap">{detail.message}</p>
                </div>
              </div>
              {detail.photos?.length > 0 && (
                <div className="mt-6">
                  <p className="text-[10px] tracking-[0.16em] uppercase text-[rgba(244,239,230,0.45)]">Photos ({detail.photos.length})</p>
                  <div className="mt-3 grid grid-cols-3 gap-3" data-testid="admin-detail-photos">
                    {detail.photos.map((p, i) => (
                      <img
                        key={i}
                        src={`data:${p.content_type};base64,${p.data}`}
                        alt={p.filename}
                        className="w-full h-28 object-cover rounded-lg border border-[color:var(--laa-hairline)]"
                      />
                    ))}
                  </div>
                </div>
              )}
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
